import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { apiRequest } from '@/lib/supabase'
import { useToast } from '@/hooks/useToast'
import { StatCardSkeleton, LinkTableSkeleton } from '@/components/ui/Skeleton'
import { getShortUrlDisplay, getShortUrl } from '@/lib/url'

function timeAgo(dateStr: string): string {
  const now = Date.now()
  const then = new Date(dateStr).getTime()
  const seconds = Math.floor((now - then) / 1000)
  if (seconds < 60) return 'Just now'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 30) return `${days}d ago`
  const months = Math.floor(days / 30)
  if (months < 12) return `${months}mo ago`
  return `${Math.floor(months / 12)}y ago`
}

function getDomain(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '')
  } catch {
    return url
  }
}

interface OverviewStats {
  total_links: number
  total_clicks: number
  links_this_month: number
  avg_clicks: number
}

interface LinkItem {
  id: string
  shortcode: string
  original_url: string
  clicks: number
  created_at: string
  custom_domain?: string
}

type StatKey = keyof OverviewStats

const STAT_CONFIG: Array<{
  key: StatKey
  label: string
  icon: string
  iconBg: string
  iconColor: string
}> = [
  {
    key: 'total_links',
    label: 'Total Links',
    icon: 'link',
    iconBg: 'bg-blue-50 dark:bg-blue-500/10',
    iconColor: 'text-[#1754cf]',
  },
  {
    key: 'total_clicks',
    label: 'Total Clicks',
    icon: 'ads_click',
    iconBg: 'bg-emerald-50 dark:bg-emerald-500/10',
    iconColor: 'text-emerald-600 dark:text-emerald-400',
  },
  {
    key: 'links_this_month',
    label: 'Links This Month',
    icon: 'calendar_month',
    iconBg: 'bg-violet-50 dark:bg-violet-500/10',
    iconColor: 'text-violet-600 dark:text-violet-400',
  },
  {
    key: 'avg_clicks',
    label: 'Avg Clicks / Link',
    icon: 'trending_up',
    iconBg: 'bg-amber-50 dark:bg-amber-500/10',
    iconColor: 'text-amber-600 dark:text-amber-400',
  },
]

export function DashboardPage() {
  const { showToast } = useToast()

  const { data: stats, isLoading: statsLoading } = useQuery<OverviewStats>({
    queryKey: ['overview'],
    queryFn: () => apiRequest<OverviewStats>('/api/overview'),
  })

  const { data: links, isLoading: linksLoading } = useQuery<LinkItem[]>({
    queryKey: ['urls'],
    queryFn: () => apiRequest<LinkItem[]>('/api/urls'),
  })

  const copyLink = async (shortcode: string, customDomain?: string) => {
    const fullUrl = getShortUrl(shortcode, customDomain)
    await navigator.clipboard.writeText(fullUrl)
    showToast('Link copied to clipboard!')
  }

  const recentLinks = links?.slice(0, 5) ?? []

  return (
    <>
      {/* Page Heading */}
      <div className="flex flex-wrap justify-between items-center gap-3 mb-8">
        <div>
          <h2 className="text-[#0d121b] dark:text-white text-2xl font-bold tracking-tight">Overview</h2>
          <p className="text-[#4c669a] dark:text-gray-400 text-sm mt-0.5">
            Real-time performance across all your shortened URLs.
          </p>
        </div>
        <button className="cursor-pointer bg-white dark:bg-white/5 border border-[#cfd7e7] dark:border-white/10 px-3.5 py-2 rounded-lg text-sm font-medium flex items-center gap-2 text-[#4c669a] dark:text-gray-400 hover:border-[#9baec8] dark:hover:border-white/20 hover:text-[#0d121b] dark:hover:text-white transition-colors duration-150">
          <span className="material-symbols-outlined text-[16px]">calendar_today</span>
          Last 30 days
        </button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {statsLoading
          ? STAT_CONFIG.map((_, i) => <StatCardSkeleton key={i} />)
          : STAT_CONFIG.map(cfg => (
              <StatCard
                key={cfg.key}
                label={cfg.label}
                value={stats?.[cfg.key] ?? 0}
                icon={cfg.icon}
                iconBg={cfg.iconBg}
                iconColor={cfg.iconColor}
              />
            ))}
      </div>

      {/* Recent Links */}
      {linksLoading ? (
        <LinkTableSkeleton rows={5} />
      ) : (
        <div className="bg-white dark:bg-[#0d121b] rounded-xl border border-[#cfd7e7] dark:border-white/10 shadow-sm overflow-hidden">
          {/* Table header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-[#e7ebf3] dark:border-white/10">
            <div className="flex items-center gap-2">
              <span className="material-symbols-outlined text-[17px] text-[#4c669a]">schedule</span>
              <h3 className="text-sm font-semibold text-[#0d121b] dark:text-white">Recent Links</h3>
            </div>
            <Link
              to="/links"
              className="text-[#1754cf] text-xs font-semibold hover:underline flex items-center gap-0.5 transition-opacity hover:opacity-80"
            >
              View all
              <span className="material-symbols-outlined text-[14px]">chevron_right</span>
            </Link>
          </div>

          {/* Empty state */}
          {recentLinks.length === 0 ? (
            <div className="px-6 py-16 flex flex-col items-center text-center">
              <div className="w-12 h-12 rounded-xl bg-[#f6f6f8] dark:bg-white/5 flex items-center justify-center mb-3">
                <span className="material-symbols-outlined text-[22px] text-[#4c669a]">link_off</span>
              </div>
              <p className="text-sm font-semibold text-[#0d121b] dark:text-white mb-1">No links yet</p>
              <p className="text-xs text-[#4c669a] dark:text-gray-400">Create your first short link to start tracking clicks.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-[#f6f6f8] dark:bg-white/[0.03] border-b border-[#e7ebf3] dark:border-white/10">
                    {['Destination', 'Short URL', 'Clicks', 'Status', 'Actions'].map(h => (
                      <th
                        key={h}
                        className={`px-6 py-3 text-[11px] font-semibold text-[#4c669a] dark:text-gray-400 uppercase tracking-wider ${h === 'Actions' ? 'text-right' : 'text-left'}`}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#f0f2f7] dark:divide-white/[0.06]">
                  {recentLinks.map((link, i) => (
                    <tr
                      key={link.id}
                      className="hover:bg-[#f8f9fc] dark:hover:bg-white/[0.02] transition-colors duration-100 animate-fadeIn"
                      style={{ animationDelay: `${i * 35}ms` }}
                    >
                      {/* Destination */}
                      <td className="px-6 py-3.5 max-w-[240px]">
                        <div className="flex items-center gap-2.5">
                          <img
                            src={`https://www.google.com/s2/favicons?domain=${getDomain(link.original_url)}&sz=32`}
                            alt=""
                            className="w-4 h-4 rounded shrink-0 opacity-80"
                            onError={e => { (e.target as HTMLImageElement).style.display = 'none' }}
                          />
                          <div className="overflow-hidden">
                            <a
                              href={link.original_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm font-medium text-[#0d121b] dark:text-white hover:text-[#1754cf] truncate block transition-colors"
                            >
                              {getDomain(link.original_url)}
                            </a>
                            <p className="text-[11px] text-[#9baec8] dark:text-gray-500 mt-0.5 truncate">
                              {timeAgo(link.created_at)}
                            </p>
                          </div>
                        </div>
                      </td>

                      {/* Short URL */}
                      <td className="px-6 py-3.5">
                        <div className="flex items-center gap-1">
                          <span className="text-sm font-mono font-medium text-[#1754cf]">
                            {getShortUrlDisplay(link.shortcode, link.custom_domain)}
                          </span>
                          <button
                            onClick={() => copyLink(link.shortcode, link.custom_domain)}
                            className="cursor-pointer p-1 text-[#9baec8] hover:text-[#1754cf] hover:bg-[#1754cf]/8 rounded transition-all duration-150"
                            title="Copy link"
                          >
                            <span className="material-symbols-outlined text-[14px]">content_copy</span>
                          </button>
                        </div>
                      </td>

                      {/* Clicks */}
                      <td className="px-6 py-3.5">
                        <div className="flex items-center gap-1.5">
                          <span className="text-sm font-semibold text-[#0d121b] dark:text-white tabular-nums">
                            {link.clicks.toLocaleString()}
                          </span>
                          {link.clicks > 0 && (
                            <span className="material-symbols-outlined text-[13px] text-emerald-500">trending_up</span>
                          )}
                        </div>
                      </td>

                      {/* Status */}
                      <td className="px-6 py-3.5">
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
                          Active
                        </span>
                      </td>

                      {/* Actions */}
                      <td className="px-6 py-3.5 text-right">
                        <Link
                          to={`/analytics?link=${link.id}`}
                          className="cursor-pointer inline-flex p-1.5 text-[#9baec8] hover:text-[#1754cf] hover:bg-[#1754cf]/8 rounded-lg transition-all duration-150"
                          title="View Analytics"
                        >
                          <span className="material-symbols-outlined text-[17px]">bar_chart</span>
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </>
  )
}

function StatCard({
  label,
  value,
  icon,
  iconBg,
  iconColor,
}: {
  label: string
  value: number
  icon: string
  iconBg: string
  iconColor: string
}) {
  return (
    <div className="bg-white dark:bg-[#0d121b] p-5 rounded-xl border border-[#cfd7e7] dark:border-white/10 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 cursor-default group">
      <div className="mb-4">
        <div
          className={`w-9 h-9 rounded-lg flex items-center justify-center transition-transform duration-200 group-hover:scale-105 ${iconBg}`}
        >
          <span className={`material-symbols-outlined text-[18px] ${iconColor}`}>{icon}</span>
        </div>
      </div>
      <p className="text-[11px] font-semibold text-[#4c669a] dark:text-gray-400 uppercase tracking-wide">
        {label}
      </p>
      <p className="text-2xl font-bold text-[#0d121b] dark:text-white tracking-tight mt-1 tabular-nums">
        {value.toLocaleString()}
      </p>
    </div>
  )
}
